import React, { useCallback, useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import myAxios from "../AxiosUrl";
import uploadService from "../../services/uploadService";
import "./TunamentPlayers.css";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Loading from "../Loading/Loading";

const REVIEW_QUEUE_ROUTE = "/TournamentPlayerReview";

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

const getDetectedAtValue = (player = {}) => player.createdAt || null;

// Player Avatar Component with Loading State
const PlayerAvatar = ({ src, alt, className }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  // All images are now stored as full CDN URLs in database
  // No need for complex URL transformation - just use the URL directly
  const imageUrl = src && src.trim() !== "" ? src : null;

  if (!imageUrl) {
    return (
      <div
        className={className}
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: "18px",
          fontWeight: "600",
        }}
      >
        {alt ? alt.charAt(0).toUpperCase() : "?"}
      </div>
    );
  }

  return (
    <div className={className} style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#6b7280",
            fontSize: "12px",
          }}
        >
          Loading...
        </div>
      )}
      {error ? (
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "18px",
            fontWeight: "600",
            width: "100%",
            height: "100%",
          }}
        >
          {alt ? alt.charAt(0).toUpperCase() : "?"}
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: loading ? "none" : "block",
          }}
        />
      )}
    </div>
  );
};

/**
 * Get proper CDN image URL for player photos
 *
 * CDN auto-generates unique filenames: {originalName}_{timestamp}_{randomId}.{ext}
 * Example: https://cdn.topsekk.com/file/Eldor_Shomurodov_1762546646769_4cm94i9tjrw.webp
 *
 * ALL images must be stored as full CDN URLs in the database
 * No relative paths or legacy formats are supported
 *
 * @param {string} imageSrc - Image source (must be full CDN URL)
 * @returns {string|null} Full CDN URL or null
 */
const getPlayerImageUrl = (imageSrc) => {
  if (!imageSrc || imageSrc.trim() === "") {
    return null;
  }

  // All images should be full CDN URLs (https://cdn.topsekk.com/file/...)
  // If it starts with http, return as-is (already a full URL)
  if (imageSrc.startsWith("http")) {
    return imageSrc;
  }

  // Legacy fallback: if somehow a relative path exists, log warning and return null
  // This should not happen in production after migration to CDN-only format
  console.warn(`[LEGACY] Non-CDN image URL detected: "${imageSrc}". Please update database to use CDN format (auto-generated by CDN)`);
  return null;
};

const DEFAULT_POSITIONS = [
  "Goalkeeper",
  "Defender",
  "Midfielder",
  "Forward",
  "Attacker",
];

const FIELD_SOURCE_LABELS = {
  MANUAL: "Manual override",
  MANUAL_OVERRIDE: "Manual override",
  MANUAL_ADJUSTMENT: "Manual +/- adjustment",
  DB: "Database",
  PE2: "Opta PE2",
  TM4: "Opta TM4",
  TM4_PRIMARY: "Opta TM4",
  TM4_FALLBACK: "Opta TM4 fallback",
  LEAGUE_OVERRIDE: "League override",
  APP_SCORING_MODEL: "App scoring model",
  CALCULATED_FROM_STORED_OPTA_MATCH_EVENTS: "Calculated from stored Opta events",
};

const ACTIVE_STATUS_OPTIONS = [
  { value: "yes", label: "Active" },
  { value: "no", label: "Inactive" },
];

const ADMIN_APPROVAL_OPTIONS = [
  { value: "true", label: "Approved" },
  { value: "false", label: "Not Approved" },
];

const PREFERRED_FOOT_OPTIONS = [
  { value: "", label: "Auto / Empty" },
  { value: "Left", label: "Left" },
  { value: "Right", label: "Right" },
  { value: "Both", label: "Both" },
];

const INJURY_STATUS_OPTIONS = [
  { value: "", label: "Auto / Empty" },
  { value: "fit", label: "Fit" },
  { value: "injured", label: "Injured" },
  { value: "doubtful", label: "Doubtful" },
  { value: "suspended", label: "Suspended" },
];

const FIELD_CONFIG = {
  firstName: {
    label: "First Name",
    type: "text",
    manualCapable: true,
    required: true,
    placeholder: "Enter first name",
  },
  lastName: {
    label: "Last Name",
    type: "text",
    manualCapable: true,
    required: true,
    placeholder: "Enter last name",
  },
  knownName: {
    label: "Known Name",
    type: "text",
    manualCapable: true,
    placeholder: "Nickname or display name",
  },
  shortFirstName: {
    label: "Short First Name",
    type: "text",
    manualCapable: true,
    placeholder: "Short first name",
  },
  shortLastName: {
    label: "Short Last Name",
    type: "text",
    manualCapable: true,
    placeholder: "Short last name",
  },
  profileImage: {
    label: "Profile Image URL",
    type: "text",
    manualCapable: true,
    placeholder: "https://...",
  },
  transfermarktUrl: {
    label: "Transfermarkt URL",
    type: "text",
    placeholder: "https://www.transfermarkt.com/...",
    externalSearch: "transfermarkt",
  },
  position: {
    label: "Position",
    type: "select",
    manualCapable: true,
    dynamicOptions: "positions",
    includeEmptyOption: true,
    emptyOptionLabel: "Select Position",
  },
  clubName: {
    label: "Club",
    type: "text",
    manualCapable: true,
    placeholder: "Enter club name",
  },
  shirtNumber: {
    label: "Shirt Number",
    type: "number",
    manualCapable: true,
    placeholder: "Enter shirt number",
    min: 1,
    max: 99,
    integer: true,
  },
  playerWorth: {
    label: "TopSekk Value",
    type: "number",
    manualCapable: true,
    placeholder: "0",
    min: 0,
  },
  totalPoints: {
    label: "Total Points",
    type: "number",
    manualCapable: true,
    placeholder: "0",
  },
  avgMatchDayPoints: {
    label: "Average Points Per Match",
    type: "number",
    manualCapable: true,
    placeholder: "0.00",
    step: "0.01",
  },
  nationality: {
    label: "Nationality",
    type: "text",
    manualCapable: true,
    placeholder: "Enter nationality",
  },
  secondNationality: {
    label: "Second Nationality",
    type: "text",
    manualCapable: true,
    placeholder: "Enter second nationality",
  },
  placeOfBirth: {
    label: "Place of Birth",
    type: "text",
    manualCapable: true,
    placeholder: "Enter place of birth",
  },
  preferredFoot: {
    label: "Preferred Foot",
    type: "select",
    manualCapable: true,
    options: PREFERRED_FOOT_OPTIONS,
  },
  injuryStatus: {
    label: "Status",
    type: "select",
    manualCapable: true,
    options: INJURY_STATUS_OPTIONS,
  },
  age: {
    label: "Age",
    type: "number",
    manualCapable: true,
    placeholder: "Age",
    min: 0,
    integer: true,
  },
  height: {
    label: "Height (cm)",
    type: "number",
    manualCapable: true,
    placeholder: "Height in cm",
    min: 0,
    integer: true,
  },
  weight: {
    label: "Weight (kg)",
    type: "number",
    manualCapable: true,
    placeholder: "Weight in kg",
    min: 0,
    integer: true,
  },
  matchesPlayed: { label: "Matches Played", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  starting11: { label: "Games Started", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  minutesPlayed: { label: "Minutes Played", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  yellowCards: { label: "Yellow Cards", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  redCards: { label: "Red Cards", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  goals: { label: "Goals", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  assists: { label: "Assists", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  totalShots: { label: "Total Shots", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  shotsOnTarget: { label: "Shots On Target", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  shotsOffTarget: { label: "Shots Off Target", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  bigChancesCreated: { label: "Big Chances Created", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  accurateCrosses: { label: "Accurate Crosses", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  touchesInOpponentBox: { label: "Touches In Opponent Box", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  successfulDribbles: { label: "Successful Dribbles", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  duelsWon: { label: "Duels Won", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  duelsLost: { label: "Duels Lost", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  totalTackles: { label: "Total Tackles", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  tacklesWon: { label: "Tackles Won", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  aerialDuelsWon: { label: "Aerial Duels Won", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  aerialDuelsLost: { label: "Aerial Duels Lost", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  totalClearances: { label: "Total Clearances", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  headClearances: { label: "Head Clearances", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  blocks: { label: "Blocks", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  blockedPasses: { label: "Blocked Passes", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  ballRecoveries: { label: "Ball Recoveries", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  saves: { label: "Saves", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  penaltySaves: { label: "Penalty Saves", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  goalsConceded: { label: "Goals Conceded", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  cleanSheets: { label: "Clean Sheets", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  accurateGoalKicks: { label: "Accurate Goal Kicks", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  accurateKeeperThrows: { label: "Accurate Keeper Throws", type: "number", manualCapable: true, placeholder: "0", min: 0, integer: true },
  matchDayPoints: { label: "Match Day Points", type: "number", placeholder: "0", min: 0, integer: true },
  highestMatchDayPoints: { label: "Highest Match Day Points", type: "number", placeholder: "0", min: 0, integer: true },
  active: { label: "Status", type: "select", options: ACTIVE_STATUS_OPTIONS },
  isAdminApproved: { label: "Admin Approved", type: "select", options: ADMIN_APPROVAL_OPTIONS },
};

const ADJUSTMENT_CAPABLE_FIELD_KEYS = new Set([
  "playerWorth",
  "totalPoints",
  "avgMatchDayPoints",
  "matchesPlayed",
  "starting11",
  "minutesPlayed",
  "yellowCards",
  "redCards",
  "goals",
  "assists",
  "totalShots",
  "shotsOnTarget",
  "shotsOffTarget",
  "bigChancesCreated",
  "accurateCrosses",
  "touchesInOpponentBox",
  "successfulDribbles",
  "duelsWon",
  "duelsLost",
  "totalTackles",
  "tacklesWon",
  "aerialDuelsWon",
  "aerialDuelsLost",
  "totalClearances",
  "headClearances",
  "blocks",
  "blockedPasses",
  "ballRecoveries",
  "saves",
  "penaltySaves",
  "goalsConceded",
  "cleanSheets",
  "accurateGoalKicks",
  "accurateKeeperThrows",
]);

ADJUSTMENT_CAPABLE_FIELD_KEYS.forEach((fieldKey) => {
  if (FIELD_CONFIG[fieldKey]) {
    FIELD_CONFIG[fieldKey].adjustmentCapable = true;
  }
});

const MANUAL_OVERRIDE_FIELD_KEYS = Object.keys(FIELD_CONFIG).filter(
  (fieldKey) => FIELD_CONFIG[fieldKey].manualCapable
);

const PLAYER_EDIT_SECTIONS = [
  {
    title: "Identity",
    icon: "badge",
    columns: 2,
    fields: [
      "firstName",
      "lastName",
      "knownName",
      "shortFirstName",
      "shortLastName",
      "profileImage",
      "transfermarktUrl",
    ],
  },
  {
    title: "Profile Basics",
    icon: "sports",
    columns: 3,
    fields: [
      "position",
      "clubName",
      "shirtNumber",
      "playerWorth",
      "totalPoints",
      "avgMatchDayPoints",
    ],
  },
  {
    title: "Player Information",
    icon: "person_pin_circle",
    columns: 3,
    fields: [
      "nationality",
      "secondNationality",
      "placeOfBirth",
      "preferredFoot",
      "injuryStatus",
      "age",
      "height",
      "weight",
    ],
  },
  {
    title: "Season Totals",
    icon: "timeline",
    columns: 3,
    fields: ["matchesPlayed", "starting11", "minutesPlayed", "yellowCards", "redCards"],
  },
  {
    title: "Offensive Statistics",
    icon: "insights",
    columns: 3,
    fields: [
      "goals",
      "assists",
      "totalShots",
      "shotsOnTarget",
      "shotsOffTarget",
      "bigChancesCreated",
      "accurateCrosses",
      "touchesInOpponentBox",
      "successfulDribbles",
    ],
  },
  {
    title: "Defensive Statistics",
    icon: "security",
    columns: 3,
    fields: [
      "duelsWon",
      "duelsLost",
      "totalTackles",
      "tacklesWon",
      "aerialDuelsWon",
      "aerialDuelsLost",
      "totalClearances",
      "headClearances",
      "blocks",
      "blockedPasses",
      "ballRecoveries",
    ],
  },
  {
    title: "Goalkeeper Statistics",
    icon: "sports_handball",
    columns: 3,
    goalkeeperOnly: true,
    fields: [
      "saves",
      "penaltySaves",
      "goalsConceded",
      "cleanSheets",
      "accurateGoalKicks",
      "accurateKeeperThrows",
    ],
  },
  {
    title: "Admin Settings",
    icon: "admin_panel_settings",
    columns: 2,
    fields: ["matchDayPoints", "highestMatchDayPoints", "active", "isAdminApproved"],
  },
];

const toComparableFieldValue = (fieldKey, value) => {
  const config = FIELD_CONFIG[fieldKey] || {};

  if (config.type === "number") {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed)
      ? (config.integer ? Math.trunc(parsed) : parsed)
      : null;
  }

  if (config.type === "select" && fieldKey === "isAdminApproved") {
    return value === true || value === "true";
  }

  if (config.type === "text" || config.type === "select") {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  }

  return value;
};

const formatFieldValueForForm = (fieldKey, value) => {
  const config = FIELD_CONFIG[fieldKey] || {};
  if (config.type === "number") {
    return value === null || value === undefined ? "" : String(value);
  }
  if (fieldKey === "isAdminApproved") {
    return value === true ? "true" : "false";
  }
  return value ?? "";
};

const normalizeFieldValueForRequest = (fieldKey, value) => {
  const config = FIELD_CONFIG[fieldKey] || {};

  if (config.type === "number") {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return config.integer ? Math.trunc(parsed) : parsed;
  }

  if (fieldKey === "isAdminApproved") {
    return value === true || value === "true";
  }

  if (config.type === "text" || config.type === "select") {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  }

  return value;
};

const formatFieldSourceLabel = (source) =>
  FIELD_SOURCE_LABELS[source] || source || "No auto source";

const formatAutoValueLabel = (fieldKey, value) => {
  const config = FIELD_CONFIG[fieldKey] || {};
  if (value === null || value === undefined || value === "") return "empty";
  if (fieldKey === "isAdminApproved") return value ? "Approved" : "Not Approved";
  if (config.type === "number") return String(value);
  return String(value);
};

const computeAdjustedFieldValue = (fieldKey, autoValue, adjustmentValue) => {
  const config = FIELD_CONFIG[fieldKey] || {};
  const autoNumber = Number(autoValue);
  const adjustmentNumber = Number(adjustmentValue);
  const safeAutoNumber = Number.isFinite(autoNumber) ? autoNumber : 0;
  const safeAdjustmentNumber = Number.isFinite(adjustmentNumber)
    ? adjustmentNumber
    : 0;

  let nextValue = safeAutoNumber + safeAdjustmentNumber;
  if (typeof config.min === "number" && nextValue < config.min) {
    nextValue = config.min;
  }

  return config.integer ? Math.trunc(nextValue) : nextValue;
};

const formatAdjustmentValueLabel = (fieldKey, value) => {
  const normalized = normalizeFieldValueForRequest(fieldKey, value);
  if (normalized === null || normalized === undefined || normalized === 0) {
    return "+0";
  }
  return normalized > 0 ? `+${normalized}` : String(normalized);
};

const getInitialAdjustmentFieldMode = (fieldMeta = {}) =>
  fieldMeta?.hasOverride ? "override" : "adjustment";

const isManualValueActiveForField = (
  fieldKey,
  value,
  mode,
  autoValue
) => {
  const config = FIELD_CONFIG[fieldKey] || {};

  if (config.adjustmentCapable) {
    if (mode === "override") {
      return (
        toComparableFieldValue(fieldKey, value) !==
        toComparableFieldValue(fieldKey, autoValue)
      );
    }

    const normalized = normalizeFieldValueForRequest(fieldKey, value);
    return normalized !== null && normalized !== undefined && normalized !== 0;
  }

  return (
    toComparableFieldValue(fieldKey, value) !==
    toComparableFieldValue(fieldKey, autoValue)
  );
};

const getInitialEditFieldValue = (fieldKey, fieldMeta = {}, fallbackValue) => {
  const config = FIELD_CONFIG[fieldKey] || {};

  if (config.adjustmentCapable) {
    if (fieldMeta.hasOverride) {
      return formatFieldValueForForm(fieldKey, fieldMeta.overrideValue);
    }

    const storedAdjustment = normalizeFieldValueForRequest(
      fieldKey,
      fieldMeta.adjustmentValue
    );
    if (
      storedAdjustment !== null &&
      storedAdjustment !== undefined &&
      storedAdjustment !== 0
    ) {
      return formatFieldValueForForm(fieldKey, storedAdjustment);
    }

    return "";
  }

  const fieldValue =
    fieldMeta.value !== undefined ? fieldMeta.value : fallbackValue;
  return formatFieldValueForForm(fieldKey, fieldValue);
};

function TournamentPlayerManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const isReviewQueueMode = location.pathname === REVIEW_QUEUE_ROUTE;
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("");
  const [clubName, setClubName] = useState("");
  const [nationality, setNationality] = useState("");
  const [active, setActive] = useState("");
  const [adminApproved, setAdminApproved] = useState("");
  const [page, setPage] = useState(1);
  const [allPlayers, setAllPlayers] = useState([]);
  const [summary, setSummary] = useState({
    totalScopedPlayers: 0,
    pendingReviewCount: 0,
    approvedCount: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    clubs: [],
    nationalities: [],
    positions: [],
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalPlayers: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [deletingPlayer, setDeletingPlayer] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editFieldMeta, setEditFieldMeta] = useState({});
  const [manualOverrideState, setManualOverrideState] = useState({});
  const [fieldEditModeState, setFieldEditModeState] = useState({});
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [detailedPlayerData, setDetailedPlayerData] = useState(null);
  const [isLoadingPlayerDetails, setIsLoadingPlayerDetails] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [approvalWorthDrafts, setApprovalWorthDrafts] = useState({});
  const [approvingPlayerId, setApprovingPlayerId] = useState(null);
  const [showTransfermarktImport, setShowTransfermarktImport] = useState(false);
  const [transfermarktCsv, setTransfermarktCsv] = useState("");
  const [transfermarktImportResult, setTransfermarktImportResult] =
    useState(null);
  const [isTransferringMarktImport, setIsTransferringMarktImport] =
    useState(false);
  const pageSize = 10;

  const activeStatuses = ACTIVE_STATUS_OPTIONS;

  const adminApprovedStatuses = ADMIN_APPROVAL_OPTIONS;

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    fetchPlayerFilters();
  }, []);

  const fetchPlayerFilters = async () => {
    try {
      const response = await myAxios.get("/tournament/players/filters");

      if (response.data.success && response.data.data) {
        setFilterOptions({
          clubs: response.data.data.teams || response.data.data.clubs || [],
          nationalities: response.data.data.nationalities || [],
          positions: response.data.data.positions || [],
        });
      }
    } catch (error) {
      console.error("Error fetching tournament player filters:", error);
    }
  };

  const fetchPlayers = useCallback(async () => {
    const startTime = Date.now();
    const minimumLoadingTime = page === 1 ? 200 : 120;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(search && { search }),
        ...(position && { position }),
        ...(clubName && { clubName }),
        ...(nationality && { nationality }),
        ...(active && { active }),
        ...(adminApproved && { adminApproved }),
        ...(isReviewQueueMode && { adminApproved: "false" }),
        ...(isReviewQueueMode && { sortBy: "createdAt" }),
        ...(isReviewQueueMode && { sortOrder: "desc" }),
      });

      const response = await myAxios.get(`/tournament/players?${params}`);

      if (response.data.success && response.data.data) {
        setAllPlayers(response.data.data);
        setPagination(response.data.pagination);
        setSummary(
          response.data.summary || {
            totalScopedPlayers: 0,
            pendingReviewCount: 0,
            approvedCount: 0,
          }
        );
      } else {
        setAllPlayers([]);
        setSummary({
          totalScopedPlayers: 0,
          pendingReviewCount: 0,
          approvedCount: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching tournament players:", error);
      setAllPlayers([]);
      setSummary({
        totalScopedPlayers: 0,
        pendingReviewCount: 0,
        approvedCount: 0,
      });
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsed);

      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }
  }, [
    active,
    adminApproved,
    clubName,
    isReviewQueueMode,
    nationality,
    page,
    position,
    search,
  ]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      // Handle no token case
    }
    fetchPlayers();
  }, [fetchPlayers]);

  const fetchDetailedPlayerData = async (playerId) => {
    setIsLoadingPlayerDetails(true);
    try {
      const response = await myAxios.get(`/tournament/players/${playerId}`);

      if (response.data.success && response.data.data) {
        setDetailedPlayerData(response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Player not found");
      }
    } catch (error) {
      console.error("Error fetching detailed player data:", error);

      // Don't show alert for now, we'll use fallback
      return null;
    } finally {
      setIsLoadingPlayerDetails(false);
    }
  };

  const positions = useMemo(() => {
    return [...new Set([...DEFAULT_POSITIONS, ...filterOptions.positions])]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [filterOptions.positions]);

  const teamOptions = useMemo(() => {
    return [
      ...new Set([
        ...filterOptions.clubs,
        ...allPlayers.map((player) => player.clubName),
      ]),
    ]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [filterOptions.clubs, allPlayers]);

  const nationalityOptions = useMemo(() => {
    return [
      ...new Set([
        ...filterOptions.nationalities,
        ...allPlayers.map((player) => player.nationality),
      ]),
    ]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [filterOptions.nationalities, allPlayers]);

  useEffect(() => {
    setPage((prevPage) => (prevPage === 1 ? prevPage : 1));
  }, [search, position, clubName, nationality, active, adminApproved, isReviewQueueMode]);

  const handlePageChange = (newPage) => {
    if (isChangingPage || newPage === page) return; // Prevent double clicks

    setIsChangingPage(true);

    // Use requestAnimationFrame for smoother transitions
    requestAnimationFrame(() => {
      setPage(newPage);

      // Reset loading state after a shorter delay
      setTimeout(() => {
        setIsChangingPage(false);
      }, 100);
    });
  };

  const handleViewPlayer = async (player) => {
    // First try to get detailed data
    const detailedData = await fetchDetailedPlayerData(player._id || player.id);

    if (detailedData) {
      // Use detailed data if available
      setSelectedPlayer(detailedData);
    } else {
      // Use existing player data as fallback
      setSelectedPlayer(player);
    }
  };

  const handleEditPlayer = async (player) => {
    // First try to get detailed data
    const detailedData = await fetchDetailedPlayerData(player._id || player.id);

    // Use detailed data if available, otherwise use existing player data
    const playerData = detailedData || player;
    const editableFieldMeta = playerData.adminMeta?.editableFieldMeta || {};
    const nextFormData = Object.keys(FIELD_CONFIG).reduce((acc, fieldKey) => {
      acc[fieldKey] = getInitialEditFieldValue(
        fieldKey,
        editableFieldMeta[fieldKey],
        playerData[fieldKey]
      );
      return acc;
    }, {});

    setEditingPlayer(playerData);
    setEditFormData(nextFormData);
    setEditFieldMeta(editableFieldMeta);
    setManualOverrideState(
      MANUAL_OVERRIDE_FIELD_KEYS.reduce((acc, fieldKey) => {
        acc[fieldKey] = FIELD_CONFIG[fieldKey]?.adjustmentCapable
          ? Boolean(editableFieldMeta[fieldKey]?.hasAdjustment) ||
            Boolean(editableFieldMeta[fieldKey]?.hasOverride)
          : Boolean(editableFieldMeta[fieldKey]?.hasOverride);
        return acc;
      }, {})
    );
    setFieldEditModeState(
      MANUAL_OVERRIDE_FIELD_KEYS.reduce((acc, fieldKey) => {
        if (!FIELD_CONFIG[fieldKey]?.adjustmentCapable) return acc;
        acc[fieldKey] = getInitialAdjustmentFieldMode(
          editableFieldMeta[fieldKey]
        );
        return acc;
      }, {})
    );
    setSelectedFile(null);
  };

  const handleDeletePlayer = async (player) => {
    setDeletingPlayer(player);
  };

  const confirmDeletePlayer = async () => {
    if (!deletingPlayer) return;

    setIsDeleting(true);
    try {
      const response = await myAxios.delete(
        `/tournament/players/${deletingPlayer._id || deletingPlayer.id}`
      );

      if (response.data.success) {
        await fetchPlayers();
        setDeletingPlayer(null);
        // You could add a success notification here
      }
    } catch (error) {
      console.error("Error deleting player:", error);
      alert(
        "Failed to delete player: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApprovalWorthDraftChange = (playerId, value) => {
    setApprovalWorthDrafts((prev) => ({
      ...prev,
      [playerId]: value,
    }));
  };

  const handleApproveWithWorth = async (player) => {
    const playerId = player._id || player.id;
    const rawWorth = String(approvalWorthDrafts[playerId] ?? "").trim();
    const parsedWorth = Number(rawWorth);

    if (!rawWorth || !Number.isFinite(parsedWorth) || parsedWorth <= 0) {
      alert(
        "Enter a positive initial market value (€) before approving this player."
      );
      return;
    }

    setApprovingPlayerId(playerId);
    try {
      const response = await myAxios.patch(
        `/tournament/players/${playerId}/approve`,
        { initialPlayerWorth: parsedWorth }
      );

      if (response.data.success) {
        setApprovalWorthDrafts((prev) => {
          const next = { ...prev };
          delete next[playerId];
          return next;
        });
        await fetchPlayers();
      } else {
        throw new Error(response.data.message || "Failed to approve player");
      }
    } catch (error) {
      console.error("Error approving player:", error);
      alert(
        "Failed to approve player: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setApprovingPlayerId(null);
    }
  };

  const handleTransfermarktImport = async (apply) => {
    if (!transfermarktCsv.trim()) {
      alert("Paste CSV content first (playerId,transfermarktUrl).");
      return;
    }

    setIsTransferringMarktImport(true);
    try {
      const response = await myAxios.post(
        "/tournament/players/transfermarkt-urls/import",
        { csv: transfermarktCsv, apply }
      );

      if (response.data.success) {
        setTransfermarktImportResult(response.data);
        if (apply && response.data.applied > 0) {
          await fetchPlayers();
        }
      } else {
        throw new Error(response.data.message || "Import failed");
      }
    } catch (error) {
      console.error("Error importing Transfermarkt URLs:", error);
      alert(
        "Import failed: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsTransferringMarktImport(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (MANUAL_OVERRIDE_FIELD_KEYS.includes(field)) {
      const config = FIELD_CONFIG[field] || {};
      const fieldMode = config.adjustmentCapable
        ? fieldEditModeState[field] ||
          getInitialAdjustmentFieldMode(editFieldMeta[field])
        : "override";

      setManualOverrideState((prev) => {
        const shouldUseManual = isManualValueActiveForField(
          field,
          value,
          fieldMode,
          editFieldMeta[field]?.autoValue
        );

        if (prev[field] === shouldUseManual) {
          return prev;
        }

        return {
          ...prev,
          [field]: shouldUseManual,
        };
      });
    }
  };

  const handleAdjustmentModeToggle = (field, useHardOverride) => {
    const config = FIELD_CONFIG[field] || {};
    if (!config.adjustmentCapable) return;

    const fieldMeta = editFieldMeta[field] || {};
    const currentMode =
      fieldEditModeState[field] || getInitialAdjustmentFieldMode(fieldMeta);
    const nextMode = useHardOverride ? "override" : "adjustment";

    if (currentMode === nextMode) return;

    const currentRawValue = editFormData[field];
    const currentNormalizedValue = normalizeFieldValueForRequest(
      field,
      currentRawValue
    );
    const normalizedAutoValue =
      normalizeFieldValueForRequest(field, fieldMeta.autoValue) ?? 0;

    let nextValue;

    if (nextMode === "override") {
      const nextFinalValue =
        currentMode === "adjustment"
          ? computeAdjustedFieldValue(
              field,
              fieldMeta.autoValue,
              manualOverrideState[field] ? currentNormalizedValue ?? 0 : 0
            )
          : currentNormalizedValue ?? normalizedAutoValue;

      nextValue = formatFieldValueForForm(field, nextFinalValue);
    } else {
      const effectiveOverrideValue =
        currentMode === "override"
          ? manualOverrideState[field]
            ? currentNormalizedValue ?? normalizedAutoValue
            : normalizedAutoValue
          : computeAdjustedFieldValue(
              field,
              fieldMeta.autoValue,
              manualOverrideState[field] ? currentNormalizedValue ?? 0 : 0
            );

      const nextAdjustmentValue = effectiveOverrideValue - normalizedAutoValue;
      nextValue =
        nextAdjustmentValue === 0
          ? ""
          : formatFieldValueForForm(field, nextAdjustmentValue);
    }

    const shouldUseManual = isManualValueActiveForField(
      field,
      nextValue,
      nextMode,
      fieldMeta.autoValue
    );

    setFieldEditModeState((prev) => ({
      ...prev,
      [field]: nextMode,
    }));
    setEditFormData((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    setManualOverrideState((prev) => ({
      ...prev,
      [field]: shouldUseManual,
    }));
  };

  const resetFieldToAuto = (field) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: FIELD_CONFIG[field]?.adjustmentCapable
        ? ""
        : formatFieldValueForForm(field, editFieldMeta[field]?.autoValue),
    }));
    setManualOverrideState((prev) => ({
      ...prev,
      [field]: false,
    }));
    if (FIELD_CONFIG[field]?.adjustmentCapable) {
      setFieldEditModeState((prev) => ({
        ...prev,
        [field]: "adjustment",
      }));
    }
  };

  const handleSaveEdit = async () => {
    if (!editingPlayer) return;

    // Basic validation
    if (!editFormData.firstName?.trim() || !editFormData.lastName?.trim()) {
      alert("First Name and Last Name are required");
      return;
    }

    setIsSubmittingEdit(true);
    try {
      const manualProfileOverrides = MANUAL_OVERRIDE_FIELD_KEYS.reduce(
        (acc, fieldKey) => {
          if (FIELD_CONFIG[fieldKey]?.adjustmentCapable) {
            const fieldMode =
              fieldEditModeState[fieldKey] ||
              getInitialAdjustmentFieldMode(editFieldMeta[fieldKey]);
            if (fieldMode !== "override") return acc;
          }
          if (!manualOverrideState[fieldKey]) return acc;
          acc[fieldKey] = normalizeFieldValueForRequest(
            fieldKey,
            editFormData[fieldKey]
          );
          return acc;
        },
        {}
      );

      const manualProfileAdjustments = MANUAL_OVERRIDE_FIELD_KEYS.reduce(
        (acc, fieldKey) => {
          if (!FIELD_CONFIG[fieldKey]?.adjustmentCapable) return acc;
          const fieldMode =
            fieldEditModeState[fieldKey] ||
            getInitialAdjustmentFieldMode(editFieldMeta[fieldKey]);
          if (fieldMode !== "adjustment") return acc;
          if (!manualOverrideState[fieldKey]) return acc;

          const adjustmentValue = normalizeFieldValueForRequest(
            fieldKey,
            editFormData[fieldKey]
          );

          if (
            adjustmentValue === null ||
            adjustmentValue === undefined ||
            adjustmentValue === 0
          ) {
            return acc;
          }

          acc[fieldKey] = adjustmentValue;
          return acc;
        },
        {}
      );

      const directUpdates = {
        ...(toComparableFieldValue("active", editFormData.active) !==
        toComparableFieldValue(
          "active",
          editFieldMeta.active?.value ?? editingPlayer?.active
        )
          ? {
              active: normalizeFieldValueForRequest("active", editFormData.active),
            }
          : {}),
        ...(toComparableFieldValue(
          "transfermarktUrl",
          editFormData.transfermarktUrl
        ) !==
        toComparableFieldValue(
          "transfermarktUrl",
          editFieldMeta.transfermarktUrl?.value ??
            editingPlayer?.transfermarktUrl ??
            ""
        )
          ? {
              transfermarktUrl: String(
                editFormData.transfermarktUrl ?? ""
              ).trim(),
            }
          : {}),
        ...(toComparableFieldValue(
          "isAdminApproved",
          editFormData.isAdminApproved
        ) !==
        toComparableFieldValue(
          "isAdminApproved",
          editFieldMeta.isAdminApproved?.value ?? editingPlayer?.isAdminApproved
        )
          ? {
              isAdminApproved: normalizeFieldValueForRequest(
                "isAdminApproved",
                editFormData.isAdminApproved
              ),
            }
          : {}),
        ...(toComparableFieldValue(
          "matchDayPoints",
          editFormData.matchDayPoints
        ) !==
        toComparableFieldValue(
          "matchDayPoints",
          editFieldMeta.matchDayPoints?.value ?? editingPlayer?.matchDayPoints
        )
          ? {
              matchDayPoints: normalizeFieldValueForRequest(
                "matchDayPoints",
                editFormData.matchDayPoints
              ) ?? 0,
            }
          : {}),
        ...(toComparableFieldValue(
          "highestMatchDayPoints",
          editFormData.highestMatchDayPoints
        ) !==
        toComparableFieldValue(
          "highestMatchDayPoints",
          editFieldMeta.highestMatchDayPoints?.value ??
            editingPlayer?.highestMatchDayPoints
        )
          ? {
              highestMatchDayPoints: normalizeFieldValueForRequest(
                "highestMatchDayPoints",
                editFormData.highestMatchDayPoints
              ) ?? 0,
            }
          : {}),
      };

      const response = await myAxios.put(
        `/tournament/players/${editingPlayer._id || editingPlayer.id}`,
        {
          manualProfileOverrides,
          manualProfileAdjustments,
          directUpdates,
        }
      );

      if (response.data.success) {
        await fetchPlayers();
        setEditingPlayer(null);
        setEditFormData({});
        setEditFieldMeta({});
        setManualOverrideState({});
        setFieldEditModeState({});
        setSelectedFile(null);
        alert("Player updated successfully!");
      } else {
        throw new Error(response.data.message || "Failed to update player");
      }
    } catch (error) {
      console.error("Error updating player:", error);
      alert(
        "Failed to update player: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const clearAllFilters = () => {
    setSearchInput("");
    setSearch("");
    setPosition("");
    setClubName("");
    setNationality("");
    setActive("");
    setAdminApproved("");
    setShowMobileFilters(false);
  };

  const isGoalkeeperEditing = useMemo(() => {
    const positionValue = String(editFormData.position || "").toLowerCase();
    return positionValue.includes("goalkeeper");
  }, [editFormData.position]);

  const renderEditField = (fieldKey) => {
    const config = FIELD_CONFIG[fieldKey];
    if (!config) return null;
    const isReadOnly = Boolean(config.readOnly);

    const fieldMeta = editFieldMeta[fieldKey] || {};
    const isAdjustmentField = Boolean(config.adjustmentCapable);
    const fieldMode = isAdjustmentField
      ? fieldEditModeState[fieldKey] ||
        getInitialAdjustmentFieldMode(fieldMeta)
      : "override";
    const isHardOverrideMode = isAdjustmentField && fieldMode === "override";
    const isManual = Boolean(manualOverrideState[fieldKey]);
    const normalizedFieldValue = normalizeFieldValueForRequest(
      fieldKey,
      editFormData[fieldKey]
    );
    const effectiveAdjustmentValue =
      normalizedFieldValue === null || normalizedFieldValue === undefined
        ? 0
        : normalizedFieldValue;
    const finalPreviewValue = isAdjustmentField
      ? isHardOverrideMode
        ? isManual
          ? normalizedFieldValue
          : fieldMeta.autoValue
        : computeAdjustedFieldValue(
            fieldKey,
            fieldMeta.autoValue,
            isManual ? effectiveAdjustmentValue : 0
          )
      : editFormData[fieldKey] ?? "";
    const sourceLabel = formatFieldSourceLabel(
      isManual
        ? isHardOverrideMode
          ? "MANUAL_OVERRIDE"
          : isAdjustmentField
            ? "MANUAL_ADJUSTMENT"
            : "MANUAL_OVERRIDE"
        : fieldMeta.autoSource
    );
    const helperLabel = isAdjustmentField
      ? isHardOverrideMode
        ? isManual
          ? `Auto: ${formatAutoValueLabel(
              fieldKey,
              fieldMeta.autoValue
            )} · Override: ${formatAutoValueLabel(
              fieldKey,
              normalizedFieldValue
            )} · Final: ${formatAutoValueLabel(fieldKey, finalPreviewValue)}`
          : `Hard override mode is ready. Final stays at auto until the value differs from ${formatAutoValueLabel(
              fieldKey,
              fieldMeta.autoValue
            )}.`
        : isManual
          ? `Auto: ${formatAutoValueLabel(
              fieldKey,
              fieldMeta.autoValue
            )} · Delta: ${formatAdjustmentValueLabel(
              fieldKey,
              effectiveAdjustmentValue
            )} · Final: ${formatAutoValueLabel(fieldKey, finalPreviewValue)}`
          : `Auto value: ${formatAutoValueLabel(
              fieldKey,
              fieldMeta.autoValue
            )} · ${formatFieldSourceLabel(fieldMeta.autoSource)}`
      : isManual
        ? `Auto value: ${formatAutoValueLabel(
            fieldKey,
            fieldMeta.autoValue
          )} · ${formatFieldSourceLabel(fieldMeta.autoSource)}`
        : `Auto source: ${sourceLabel}`;

    const options =
      config.dynamicOptions === "positions"
        ? [
            ...(config.includeEmptyOption
              ? [
                  {
                    value: "",
                    label: config.emptyOptionLabel || "Select",
                  },
                ]
              : []),
            ...positions.map((option) => ({
              value: option,
              label: option,
            })),
          ]
        : config.options || [];

    return (
      <div
        key={fieldKey}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <label
            style={{
              fontWeight: "600",
              color: "#374151",
              fontSize: "14px",
            }}
          >
            {config.label}
            {config.required ? " *" : ""}
          </label>

          {config.manualCapable && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.02em",
                  background: isManual ? "#dcfce7" : "#eff6ff",
                  color: isManual ? "#166534" : "#1d4ed8",
                }}
              >
                {sourceLabel}
              </span>
              {isManual && (
                <button
                  type="button"
                  onClick={() => resetFieldToAuto(fieldKey)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#2563eb",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Use Auto
                </button>
              )}
            </div>
          )}
        </div>

        {isAdjustmentField ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "10px",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: "4px",
                  }}
                >
                  Auto
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "#0f172a",
                  }}
                >
                  {formatAutoValueLabel(fieldKey, fieldMeta.autoValue)}
                </div>
              </div>

              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: isManual ? "#ecfdf5" : "#f8fafc",
                  border: `1px solid ${isManual ? "#bbf7d0" : "#e2e8f0"}`,
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: "4px",
                  }}
                >
                  {isHardOverrideMode ? "Override" : "Admin +/-"}
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: isManual ? "#166534" : "#0f172a",
                  }}
                >
                  {isHardOverrideMode
                    ? formatAutoValueLabel(
                        fieldKey,
                        isManual ? normalizedFieldValue : fieldMeta.autoValue
                      )
                    : formatAdjustmentValueLabel(
                        fieldKey,
                        isManual ? effectiveAdjustmentValue : 0
                      )}
                </div>
              </div>

              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: "#fefce8",
                  border: "1px solid #fde68a",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: "4px",
                  }}
                >
                  Final
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "#92400e",
                  }}
                >
                  {formatAutoValueLabel(fieldKey, finalPreviewValue)}
                </div>
              </div>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#334155",
                opacity: isReadOnly ? 0.6 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={isHardOverrideMode}
                disabled={isReadOnly}
                onChange={(e) =>
                  handleAdjustmentModeToggle(fieldKey, e.target.checked)
                }
              />
              Hard override this value
            </label>

            <input
              type="number"
              value={editFormData[fieldKey] ?? ""}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              onChange={(e) => handleEditFormChange(fieldKey, e.target.value)}
              placeholder={isHardOverrideMode ? "Set final value" : "-2 / +5"}
              min={isHardOverrideMode ? config.min : undefined}
              max={isHardOverrideMode ? config.max : undefined}
              step={config.step}
              style={{
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                transition: "all 0.3s ease",
                background: isReadOnly ? "#f8fafc" : "white",
              }}
            />
          </div>
        ) : config.type === "select" ? (
          <select
            value={editFormData[fieldKey] ?? ""}
            disabled={isReadOnly}
            onChange={(e) => handleEditFormChange(fieldKey, e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "14px",
              transition: "all 0.3s ease",
              background: isReadOnly ? "#f8fafc" : "white",
            }}
          >
            {options.map((option) => (
              <option key={`${fieldKey}-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={config.type}
            value={editFormData[fieldKey] ?? ""}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            onChange={(e) => handleEditFormChange(fieldKey, e.target.value)}
            placeholder={config.placeholder || ""}
            min={config.min}
            max={config.max}
            step={config.step}
            required={config.required}
            style={{
              padding: "12px 16px",
              border: "2px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "14px",
              transition: "all 0.3s ease",
              background: isReadOnly ? "#f8fafc" : "white",
            }}
          />
        )}

        {config.externalSearch === "transfermarkt" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <a
              href={`https://www.transfermarkt.com/schnellsuche/ergebnis/schnellsuche?query=${encodeURIComponent(
                [editFormData.firstName, editFormData.lastName]
                  .filter(Boolean)
                  .join(" ")
              )}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: "700",
                color: "#1d4ed8",
                textDecoration: "none",
              }}
            >
              <span className="material-icons" style={{ fontSize: "16px" }}>
                open_in_new
              </span>
              Find on Transfermarkt
            </a>
            <span
              style={{
                fontSize: "12px",
                color: "#64748b",
                lineHeight: 1.4,
              }}
            >
              Opens a current Transfermarkt name search. Use the club shown in
              the results to select the right profile, then paste its URL above.
            </span>
          </div>
        )}

        {(config.manualCapable || isReadOnly) && (
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              lineHeight: 1.4,
            }}
          >
            {isReadOnly
              ? "System-owned field. Points are read-only in admin and update only from match processing."
              : helperLabel}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    setSearchInput("");
    setSearch("");
    setPosition("");
    setClubName("");
    setNationality("");
    setActive("");
    setAdminApproved("");
    setShowMobileFilters(false);
    setPage(1);
  }, [isReviewQueueMode]);

  const activeFilterCount = [
    searchInput,
    position,
    clubName,
    nationality,
    active,
    !isReviewQueueMode ? adminApproved : "",
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;
  const hasData = allPlayers.length > 0;
  const isPlayersRefreshing = loading || isChangingPage;

  const getPageNumbers = () => {
    const numbers = [];
    const maxVisible = 5;
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        numbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) numbers.push(i);
        numbers.push("...");
        numbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        numbers.push(1);
        numbers.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) numbers.push(i);
      } else {
        numbers.push(1);
        numbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          numbers.push(i);
        numbers.push("...");
        numbers.push(totalPages);
      }
    }

    return numbers;
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.25 },
    },
  };

  const tableRowVariants = {
    hidden: {
      opacity: 0,
      x: -15,
    },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.2,
        delay: i * 0.02,
        ease: "easeOut",
      },
    }),
    exit: {
      opacity: 0,
      x: 15,
      transition: { duration: 0.15 },
    },
  };

  return (
    <motion.div
      className="tournament-player-mgmt-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div
        className="tournament-player-mgmt-header"
        variants={itemVariants}
      >
        <div className="tournament-player-mgmt-header-left">
          <motion.span
            className="material-icons tournament-player-mgmt-header-icon"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            sports_soccer
          </motion.span>
          <div>
            <h2 className="tournament-player-mgmt-title">
              {isReviewQueueMode ? "New Player Review" : "Tournament Players"}
            </h2>
            <p
              style={{
                margin: "6px 0 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              {isReviewQueueMode
                ? "Newly detected players stay here until an admin approves them for the app."
                : "Manage synced tournament players and control which ones are visible in the mobile app."}
            </p>
          </div>
        </div>

        <div className="tournament-player-mgmt-header-actions">
          {isReviewQueueMode && (
            <motion.button
              className="tournament-player-mgmt-header-clear-btn"
              onClick={() =>
                setShowTransfermarktImport((prev) => !prev)
              }
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="material-icons">link</span>
              Bulk Transfermarkt URLs
            </motion.button>
          )}
          <motion.button
            className="tournament-player-mgmt-header-clear-btn"
            onClick={() =>
              navigate(isReviewQueueMode ? "/TunamentPlayers" : REVIEW_QUEUE_ROUTE)
            }
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="material-icons">
              {isReviewQueueMode ? "view_list" : "playlist_add_check"}
            </span>
            {isReviewQueueMode ? "All Players" : "Review Queue"}
          </motion.button>

          {hasActiveFilters && (
            <motion.button
              className="tournament-player-mgmt-header-clear-btn"
              onClick={clearAllFilters}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="material-icons">clear_all</span>
              Clear All
            </motion.button>
          )}

          <motion.button
            className="tournament-player-mgmt-mobile-filter-btn"
            onClick={() => setShowMobileFilters(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="material-icons">filter_list</span>
            Filters
            {hasActiveFilters && (
              <span className="tournament-player-mgmt-filter-badge">
                {activeFilterCount}
              </span>
            )}
          </motion.button>
        </div>
      </motion.div>

      {isReviewQueueMode && showTransfermarktImport && (
        <motion.div
          variants={itemVariants}
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "24px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <span className="material-icons" style={{ color: "#1d4ed8" }}>
              link
            </span>
            <strong>Bulk Transfermarkt URL import</strong>
          </div>
          <p
            style={{
              margin: "0 0 12px",
              color: "#6b7280",
              fontSize: "13px",
            }}
          >
            Paste CSV rows as <code>playerId,transfermarktUrl</code> (one per
            line; an empty URL clears the stored link). Preview first — nothing
            is written until you press Apply.
          </p>
          <textarea
            value={transfermarktCsv}
            onChange={(e) => setTransfermarktCsv(e.target.value)}
            rows={6}
            placeholder={"playerId,transfermarktUrl\n2xj9f...,https://www.transfermarkt.com/..."}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "2px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "13px",
              fontFamily: "monospace",
              boxSizing: "border-box",
              marginBottom: "12px",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="tournament-player-mgmt-header-clear-btn"
              disabled={isTransferringMarktImport}
              onClick={() => handleTransfermarktImport(false)}
              style={{ opacity: isTransferringMarktImport ? 0.6 : 1 }}
            >
              <span className="material-icons">visibility</span>
              Preview
            </button>
            <button
              type="button"
              className="tournament-player-mgmt-header-clear-btn"
              disabled={isTransferringMarktImport}
              onClick={() => handleTransfermarktImport(true)}
              style={{ opacity: isTransferringMarktImport ? 0.6 : 1 }}
            >
              <span className="material-icons">upload_file</span>
              Apply
            </button>
            {isTransferringMarktImport && <span>Working…</span>}
          </div>
          {transfermarktImportResult && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                borderRadius: "8px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              <strong>
                {transfermarktImportResult.mode === "apply"
                  ? "Apply result"
                  : "Preview result"}
                :
              </strong>{" "}
              {transfermarktImportResult.matched} matched,{" "}
              {transfermarktImportResult.unmatched} unmatched,{" "}
              {transfermarktImportResult.totalValidRows} valid rows
              {transfermarktImportResult.mode === "apply" &&
                ` (${transfermarktImportResult.applied} applied, ${transfermarktImportResult.cleared} cleared)`}
              .
              {transfermarktImportResult.parseErrors?.length > 0 && (
                <div style={{ color: "#b91c1c", marginTop: "6px" }}>
                  {transfermarktImportResult.parseErrors
                    .map((rowError) => `Line ${rowError.line}: ${rowError.reason}`)
                    .join(" · ")}
                </div>
              )}
              {transfermarktImportResult.unmatchedPlayerIds?.length > 0 && (
                <div style={{ color: "#b45309", marginTop: "6px" }}>
                  Unknown player IDs:{" "}
                  {transfermarktImportResult.unmatchedPlayerIds.join(", ")}
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        variants={itemVariants}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        {[
          {
            label: "Not Approved",
            value: summary.pendingReviewCount || 0,
            icon: "person_search",
            color: "#f59e0b",
            bg: "#fff7ed",
          },
          {
            label: "Approved",
            value: summary.approvedCount || 0,
            icon: "verified_user",
            color: "#10b981",
            bg: "#ecfdf5",
          },
          {
            label: "Scoped Total",
            value: summary.totalScopedPlayers || 0,
            icon: "groups",
            color: "#2563eb",
            bg: "#eff6ff",
          },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              background: card.bg,
              border: "1px solid rgba(148, 163, 184, 0.16)",
              borderRadius: "16px",
              padding: "18px 20px",
              boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <span style={{ color: "#475569", fontSize: "13px", fontWeight: "600" }}>
                {card.label}
              </span>
              <span className="material-icons" style={{ color: card.color }}>
                {card.icon}
              </span>
            </div>
            <div style={{ fontSize: "30px", fontWeight: "700", color: "#0f172a" }}>
              {card.value}
            </div>
          </div>
        ))}
      </motion.div>

      <>
          {/* Filters */}
          <motion.div
            className="tournament-player-mgmt-filters tournament-player-mgmt-desktop-filters"
            variants={itemVariants}
          >
            <div className="tournament-player-mgmt-filters-grid">
              <div className="tournament-player-mgmt-filter-group">
                <div className="tournament-player-mgmt-filter-box">
                  <span className="material-icons tournament-player-mgmt-filter-icon">
                    sports
                  </span>
                  <div className="tournament-player-mgmt-select-wrapper">
                    <select
                      className="tournament-player-mgmt-native-select"
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                    >
                      <option value="">All Positions</option>
                      {positions.map((pos) => (
                        <option key={pos} value={pos}>
                          {pos}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="tournament-player-mgmt-filter-group">
                <div className="tournament-player-mgmt-filter-box">
                  <span className="material-icons tournament-player-mgmt-filter-icon">
                    groups
                  </span>
                  <div className="tournament-player-mgmt-select-wrapper">
                    <select
                      className="tournament-player-mgmt-native-select"
                      value={clubName}
                      onChange={(e) => setClubName(e.target.value)}
                    >
                      <option value="">All Teams</option>
                      {teamOptions.map((team) => (
                        <option key={team} value={team}>
                          {team}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="tournament-player-mgmt-filter-group">
                <div className="tournament-player-mgmt-filter-box">
                  <span className="material-icons tournament-player-mgmt-filter-icon">
                    flag
                  </span>
                  <div className="tournament-player-mgmt-select-wrapper">
                    <select
                      className="tournament-player-mgmt-native-select"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                    >
                      <option value="">All Nationalities</option>
                      {nationalityOptions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="tournament-player-mgmt-filter-group">
                <div className="tournament-player-mgmt-filter-box">
                  <span className="material-icons tournament-player-mgmt-filter-icon">
                    check_circle
                  </span>
                  <div className="tournament-player-mgmt-select-wrapper">
                    <select
                      className="tournament-player-mgmt-native-select"
                      value={active}
                      onChange={(e) => setActive(e.target.value)}
                    >
                      <option value="">All Status</option>
                      {activeStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {!isReviewQueueMode && (
                <div className="tournament-player-mgmt-filter-group">
                  <div className="tournament-player-mgmt-filter-box">
                    <span className="material-icons tournament-player-mgmt-filter-icon">
                      verified_user
                    </span>
                    <div className="tournament-player-mgmt-select-wrapper">
                      <select
                        className="tournament-player-mgmt-native-select"
                        value={adminApproved}
                        onChange={(e) => setAdminApproved(e.target.value)}
                      >
                        <option value="">All Admin Approval</option>
                        {adminApprovedStatuses.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="tournament-player-mgmt-filter-group">
                <div className="tournament-player-mgmt-filter-box">
                  <span className="material-icons tournament-player-mgmt-filter-icon">
                    search
                  </span>
                  <input
                    type="text"
                    className="tournament-player-mgmt-filter-input"
                    placeholder="Search by name, team or nationality..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile Cards for smaller screens */}
          <motion.div
            className="tournament-player-mgmt-mobile-cards"
            variants={itemVariants}
          >
            {!hasData && isPlayersRefreshing ? (
              <div className="tournament-player-mgmt-mobile-loading-state">
                <Loading message="Loading players..." size="small" />
              </div>
            ) : !hasData ? (
              <div className="tournament-player-mgmt-no-data-static">
                <span className="material-icons tournament-player-mgmt-no-data-icon-static">
                  sports_soccer
                </span>
                <h3 className="tournament-player-mgmt-no-data-title-static">
                  {isReviewQueueMode
                    ? "No Players Waiting For Approval"
                    : "No Tournament Players Found"}
                </h3>
                <p className="tournament-player-mgmt-no-data-desc-static">
                  {hasActiveFilters
                    ? "No players match your current filters. Try adjusting your search criteria."
                    : isReviewQueueMode
                      ? "All newly detected players have already been approved."
                      : "No tournament players available at the moment."}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="tournament-player-mgmt-no-data-btn-static"
                  >
                    <span className="material-icons">clear_all</span>
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <AnimatePresence>
                  {allPlayers.map((player, index) => (
                    <motion.div
                      key={player._id || player.id || index}
                      className="tournament-player-mgmt-mobile-card"
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      layout
                    >
                      <div className="tournament-player-mgmt-mobile-card-header">
                        <div className="tournament-player-mgmt-mobile-card-number">
                          Player #{(page - 1) * pageSize + index + 1}
                        </div>
                        <div className="tournament-player-mgmt-mobile-card-actions">
                          <motion.button
                            className="tournament-player-mgmt-action-btn tournament-player-mgmt-view-btn"
                            onClick={() => handleViewPlayer(player)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span className="material-icons">visibility</span>
                          </motion.button>
                          <motion.button
                            className="tournament-player-mgmt-action-btn tournament-player-mgmt-edit-btn"
                            onClick={() => handleEditPlayer(player)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span className="material-icons">edit</span>
                          </motion.button>
                          <motion.button
                            className="tournament-player-mgmt-action-btn tournament-player-mgmt-delete-btn"
                            onClick={() => handleDeletePlayer(player)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span className="material-icons">delete</span>
                          </motion.button>
                        </div>
                      </div>
                      <div className="tournament-player-mgmt-mobile-card-content">
                        <div className="tournament-player-mgmt-mobile-card-field">
                          <label>Player:</label>
                          <div className="tournament-player-mgmt-mobile-card-value">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <PlayerAvatar
                                src={player.profileImageUrl}
                                alt={`${player.firstName} ${player.lastName}`}
                                className="tournament-player-mgmt-player-avatar"
                              />
                              <div>
                                <div
                                  style={{ fontWeight: "600", fontSize: "14px" }}
                                >
                                  {player.firstName} {player.lastName}
                                </div>
                                {player.knownName && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#6b7280",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    "{player.knownName}"
                                  </div>
                                )}
                                {isReviewQueueMode && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#64748b",
                                      marginTop: "4px",
                                    }}
                                  >
                                    Detected: {formatDateTime(getDetectedAtValue(player))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="tournament-player-mgmt-mobile-card-field">
                          <label>Admin Approval:</label>
                          <div className="tournament-player-mgmt-mobile-card-value">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <span
                                className={`material-icons ${
                                  player.isAdminApproved
                                    ? "tournament-player-mgmt-approved"
                                    : "tournament-player-mgmt-not-approved"
                                }`}
                                style={{ fontSize: "16px" }}
                              >
                                verified_user
                              </span>
                              <span
                                style={{
                                  color: player.isAdminApproved
                                    ? "#10b981"
                                    : "#ef4444",
                                  fontWeight: "600",
                                }}
                              >
                                {player.isAdminApproved ? "Approved" : "Not Approved"}
                              </span>
                            </div>
                            {isReviewQueueMode && !player.isAdminApproved && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  marginTop: "6px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <input
                                  type="number"
                                  min="1"
                                  step="50000"
                                  placeholder="Initial value €"
                                  value={
                                    approvalWorthDrafts[player._id || player.id] ?? ""
                                  }
                                  onChange={(e) =>
                                    handleApprovalWorthDraftChange(
                                      player._id || player.id,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    width: "130px",
                                    padding: "6px 8px",
                                    border: "2px solid #e5e7eb",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                  }}
                                />
                                <button
                                  type="button"
                                  className="tournament-player-mgmt-action-btn tournament-player-mgmt-edit-btn"
                                  title="Approve with initial market value"
                                  disabled={
                                    approvingPlayerId === (player._id || player.id)
                                  }
                                  onClick={() => handleApproveWithWorth(player)}
                                >
                                  <span className="material-icons">
                                    {approvingPlayerId === (player._id || player.id)
                                      ? "hourglass_empty"
                                      : "check_circle"}
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="tournament-player-mgmt-mobile-card-field">
                          <label>Position:</label>
                          <div className="tournament-player-mgmt-mobile-card-value">
                            <span className="material-icons tournament-player-mgmt-mobile-card-icon">
                              sports
                            </span>
                            {player.position || "-"}
                          </div>
                        </div>
                        <div className="tournament-player-mgmt-mobile-card-field">
                          <label>Club:</label>
                          <div className="tournament-player-mgmt-mobile-card-value">
                            <span className="material-icons tournament-player-mgmt-mobile-card-icon">
                              business
                            </span>
                            {player.clubName || "-"}
                          </div>
                        </div>
                        <div className="tournament-player-mgmt-mobile-card-field">
                          <label>Points:</label>
                          <div className="tournament-player-mgmt-mobile-card-value">
                            <span
                              className="material-icons tournament-player-mgmt-mobile-card-icon"
                              style={{ color: "#f59e0b" }}
                            >
                              star
                            </span>
                            <strong>{player.totalPoints || 0}</strong>
                          </div>
                        </div>
                        {player.age && (
                          <div className="tournament-player-mgmt-mobile-card-field">
                            <label>Age:</label>
                            <div className="tournament-player-mgmt-mobile-card-value">
                              {player.age} years
                            </div>
                          </div>
                        )}
                        {player.nationality && (
                          <div className="tournament-player-mgmt-mobile-card-field">
                            <label>Nationality:</label>
                            <div className="tournament-player-mgmt-mobile-card-value">
                              {player.nationality}
                            </div>
                          </div>
                        )}
                        <div className="tournament-player-mgmt-mobile-card-field">
                          <label>Status:</label>
                          <div className="tournament-player-mgmt-mobile-card-value">
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "600",
                                background:
                                  player.active === "yes" ? "#dcfce7" : "#fef2f2",
                                color:
                                  player.active === "yes" ? "#166534" : "#dc2626",
                              }}
                            >
                              {player.active === "yes" ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {isPlayersRefreshing && (
                  <div className="tournament-player-mgmt-players-overlay tournament-player-mgmt-mobile-players-overlay">
                    <Loading message="Refreshing players..." size="small" />
                  </div>
                )}
              </>
            )}
          </motion.div>

          {/* Table */}
          <motion.div
            className="tournament-player-mgmt-tickets-grid"
            variants={itemVariants}
          >
            <div
              className={`tournament-player-mgmt-table-container tournament-player-mgmt-desktop-table ${
                isPlayersRefreshing ? "loading" : ""
              }`}
            >
              <table className="tournament-player-mgmt-table">
                <thead>
                  <tr>
                    <th className="tournament-player-mgmt-col-competition">
                      <span className="material-icons tournament-player-mgmt-th-icon">
                        person
                      </span>
                      Player
                    </th>
                    <th className="tournament-player-mgmt-col-admin-approval">
                      <span className="material-icons tournament-player-mgmt-th-icon">
                        verified_user
                      </span>
                      Admin Approval
                    </th>
                    <th className="tournament-player-mgmt-col-country">
                      <span className="material-icons tournament-player-mgmt-th-icon">
                        sports
                      </span>
                      Position
                    </th>
                    <th className="tournament-player-mgmt-col-type">
                      <span className="material-icons tournament-player-mgmt-th-icon">
                        groups
                      </span>
                      Club
                    </th>
                    <th className="tournament-player-mgmt-col-format">
                      <span className="material-icons tournament-player-mgmt-th-icon">
                        star
                      </span>
                      Points
                    </th>
                    <th className="tournament-player-mgmt-col-actions">
                      <span className="material-icons tournament-player-mgmt-th-icon">
                        settings
                      </span>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`tournament-player-mgmt-table-body ${
                    isPlayersRefreshing ? "loading" : ""
                  }`}
                >
                  {!hasData && isPlayersRefreshing ? (
                    <tr className="tournament-player-mgmt-table-loading-row">
                      <td colSpan="6">
                        <span className="material-icons tournament-player-mgmt-table-loading-spinner">
                          sync
                        </span>
                        Loading players...
                      </td>
                    </tr>
                  ) : !hasData ? (
                    <tr className="tournament-player-mgmt-no-data-row">
                      <td
                        colSpan="6"
                        className="tournament-player-mgmt-no-data-cell"
                      >
                        <div className="tournament-player-mgmt-no-data-content">
                          <div className="tournament-player-mgmt-no-data-icon-container">
                            <span className="material-icons tournament-player-mgmt-no-data-icon">
                              sports_soccer
                            </span>
                            <div className="tournament-player-mgmt-no-data-icon-bg"></div>
                          </div>
                          <div className="tournament-player-mgmt-no-data-text">
                            <h4>
                              {isReviewQueueMode
                                ? "No Players Waiting For Approval"
                                : "No Tournament Players Found"}
                            </h4>
                            <p>
                              {hasActiveFilters
                                ? "No players match your current filters. Try adjusting your search criteria."
                                : isReviewQueueMode
                                  ? "All newly detected players have already been approved."
                                  : "No tournament players available at the moment."}
                            </p>
                          </div>
                          {hasActiveFilters && (
                            <button
                              onClick={clearAllFilters}
                              className="tournament-player-mgmt-clear-filters-btn-inline"
                            >
                              <span className="material-icons">clear_all</span>
                              Clear All Filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <AnimatePresence mode="wait">
                      {allPlayers.map((player, index) => (
                        <motion.tr
                          key={player._id || player.id || index}
                          className="tournament-player-mgmt-table-row"
                          variants={tableRowVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          custom={index}
                          layout
                        >
                            <td className="tournament-player-mgmt-col-competition">
                              <div className="tournament-player-mgmt-competition-cell">
                                <PlayerAvatar
                                  src={player.profileImageUrl}
                                  alt={`${player.firstName} ${player.lastName}`}
                                  className="tournament-player-mgmt-player-avatar"
                                />
                                <div className="tournament-player-mgmt-player-info">
                                  <div className="tournament-player-mgmt-player-name">
                                    {player.firstName} {player.lastName}
                                  </div>
                                  <div className="tournament-player-mgmt-player-known">
                                    {player.knownName || ""}
                                  </div>
                                  {isReviewQueueMode && (
                                    <div className="tournament-player-mgmt-player-known">
                                      Detected: {formatDateTime(getDetectedAtValue(player))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="tournament-player-mgmt-col-admin-approval">
                              <div className="tournament-player-mgmt-admin-approval-cell">
                                <span
                                  className={`material-icons ${
                                    player.isAdminApproved
                                      ? "tournament-player-mgmt-approved"
                                      : "tournament-player-mgmt-not-approved"
                                  }`}
                                >
                                  verified_user
                                </span>
                                <span className="tournament-player-mgmt-approval-text">
                                  {player.isAdminApproved
                                    ? "Approved"
                                    : "Not Approved"}
                                </span>
                              </div>
                              {isReviewQueueMode && !player.isAdminApproved && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    marginTop: "6px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <input
                                    type="number"
                                    min="1"
                                    step="50000"
                                    placeholder="Value €"
                                    value={
                                      approvalWorthDrafts[
                                        player._id || player.id
                                      ] ?? ""
                                    }
                                    onChange={(e) =>
                                      handleApprovalWorthDraftChange(
                                        player._id || player.id,
                                        e.target.value
                                      )
                                    }
                                    style={{
                                      width: "110px",
                                      padding: "6px 8px",
                                      border: "2px solid #e5e7eb",
                                      borderRadius: "6px",
                                      fontSize: "13px",
                                    }}
                                  />
                                  <motion.button
                                    type="button"
                                    className="tournament-player-mgmt-action-btn tournament-player-mgmt-edit-btn"
                                    title="Approve with initial market value"
                                    disabled={
                                      approvingPlayerId ===
                                      (player._id || player.id)
                                    }
                                    onClick={() => handleApproveWithWorth(player)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ duration: 0.15 }}
                                  >
                                    <span className="material-icons">
                                      {approvingPlayerId ===
                                      (player._id || player.id)
                                        ? "hourglass_empty"
                                        : "check_circle"}
                                    </span>
                                  </motion.button>
                                </div>
                              )}
                            </td>
                            <td className="tournament-player-mgmt-col-country">
                              <div className="tournament-player-mgmt-position-cell">
                                <span className="material-icons">sports</span>
                                <span>{player.position || "-"}</span>
                              </div>
                            </td>
                            <td className="tournament-player-mgmt-col-type">
                              <div className="tournament-player-mgmt-club-cell">
                                <span className="material-icons">business</span>
                                <span>{player.clubName || "-"}</span>
                              </div>
                            </td>
                            <td className="tournament-player-mgmt-col-format">
                              <div className="tournament-player-mgmt-points-cell">
                                <span className="material-icons">star</span>
                                <span>{player.totalPoints || 0}</span>
                              </div>
                            </td>
                            <td className="tournament-player-mgmt-col-actions">
                              <div className="tournament-player-mgmt-action-btns">
                                <motion.button
                                  className="tournament-player-mgmt-action-btn tournament-player-mgmt-view-btn"
                                  onClick={() => handleViewPlayer(player)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <span className="material-icons">
                                    visibility
                                  </span>
                                </motion.button>
                                <motion.button
                                  className="tournament-player-mgmt-action-btn tournament-player-mgmt-edit-btn"
                                  onClick={() => handleEditPlayer(player)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <span className="material-icons">edit</span>
                                </motion.button>
                                <motion.button
                                  className="tournament-player-mgmt-action-btn tournament-player-mgmt-delete-btn"
                                  onClick={() => handleDeletePlayer(player)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  <span className="material-icons">delete</span>
                                </motion.button>
                              </div>
                            </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  )}
                </tbody>
              </table>

              {hasData && isPlayersRefreshing && (
                <div className="tournament-player-mgmt-players-overlay">
                  <Loading message="Refreshing players..." size="small" />
                </div>
              )}
            </div>

            {/* Pagination */}
            {hasData && pagination.totalPages > 1 && (
              <motion.div
                className="tournament-player-mgmt-pagination"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  // Keep pagination visible during page changes
                  filter: isChangingPage ? "brightness(0.8)" : "brightness(1)",
                }}
                transition={{
                  delay: 0.2,
                  filter: { duration: 0.3 },
                }}
                style={{
                  // Prevent layout shift during loading
                  minHeight: "80px",
                  position: "relative",
                }}
              >
                {/* Loading overlay for pagination */}
                {isChangingPage && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(2px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "0 0 16px 16px",
                    }}
                  >
                    <motion.span
                      className="material-icons"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      style={{
                        fontSize: "24px",
                        color: "#6366f1",
                      }}
                    >
                      sync
                    </motion.span>
                  </motion.div>
                )}

                <div className="tournament-player-mgmt-pagination-total">
                  <span className="material-icons">list_alt</span>
                  <span>
                    Total: <strong>{pagination.totalPlayers}</strong> players
                  </span>
                </div>

                <motion.button
                  type="button"
                  className={`tournament-player-mgmt-pagination-nav ${
                    page === 1 ? "disabled" : ""
                  }`}
                  onClick={() => handlePageChange(Math.max(1, page - 1))}
                  disabled={page === 1 || isChangingPage}
                  whileHover={{
                    scale: page !== 1 && !isChangingPage ? 1.05 : 1,
                  }}
                  whileTap={{ scale: page !== 1 && !isChangingPage ? 0.95 : 1 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    opacity: isChangingPage ? 0.6 : 1,
                    pointerEvents: isChangingPage ? "none" : "auto",
                  }}
                >
                  <span className="material-icons">chevron_left</span>
                </motion.button>

                <div className="tournament-player-mgmt-pagination-numbers">
                  {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="tournament-player-mgmt-pagination-ellipsis"
                      >
                        ...
                      </span>
                    ) : (
                      <motion.button
                        key={pageNum}
                        type="button"
                        className={`tournament-player-mgmt-pagination-num ${
                          pageNum === page ? "active" : ""
                        }`}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={isChangingPage}
                        layout
                        whileHover={{
                          scale: pageNum !== page && !isChangingPage ? 1.1 : 1,
                        }}
                        whileTap={{
                          scale: pageNum !== page && !isChangingPage ? 0.9 : 1,
                        }}
                        transition={{ duration: 0.15 }}
                        style={{
                          opacity: isChangingPage ? 0.6 : 1,
                          pointerEvents: isChangingPage ? "none" : "auto",
                        }}
                      >
                        {pageNum}
                      </motion.button>
                    )
                  )}
                </div>

                <motion.button
                  type="button"
                  className={`tournament-player-mgmt-pagination-nav ${
                    page === pagination.totalPages ? "disabled" : ""
                  }`}
                  onClick={() =>
                    handlePageChange(Math.min(pagination.totalPages, page + 1))
                  }
                  disabled={page === pagination.totalPages || isChangingPage}
                  whileHover={{
                    scale:
                      page !== pagination.totalPages && !isChangingPage
                        ? 1.05
                        : 1,
                  }}
                  whileTap={{
                    scale:
                      page !== pagination.totalPages && !isChangingPage
                        ? 0.95
                        : 1,
                  }}
                  transition={{ duration: 0.15 }}
                  style={{
                    opacity: isChangingPage ? 0.6 : 1,
                    pointerEvents: isChangingPage ? "none" : "auto",
                  }}
                >
                  <span className="material-icons">chevron_right</span>
                </motion.button>

                <div className="tournament-player-mgmt-pagination-info">
                  Page {page} of {pagination.totalPages}
                </div>
              </motion.div>
            )}
          </motion.div>
      </>

      {/* Enhanced View Player Modal */}
      {selectedPlayer && (
        <Modal
          open={!!selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          aria-labelledby="view-player-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "700px" },
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 0,
              maxHeight: "90vh",
              overflow: "auto",
              p: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                maxHeight: "90vh",
                overflow: "hidden",
                background: "#ffffff",
                borderRadius: 0,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  padding: "24px",
                  borderRadius: 0,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    content: "",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(45deg, rgba(255, 255, 255, 0.1), transparent)",
                    pointerEvents: "none",
                  }}
                ></div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      flex: 1,
                    }}
                  >
                    <PlayerAvatar
                      src={
                        selectedPlayer.profileImageUrl ||
                        selectedPlayer.profileImage
                      }
                      alt={`${selectedPlayer.firstName} ${selectedPlayer.lastName}`}
                      className="tournament-player-details-avatar"
                      style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "3px solid rgba(255, 255, 255, 0.3)",
                        boxShadow: "0 8px 20px rgba(0, 0, 0, 0.2)",
                        transition: "transform 0.3s ease",
                        background: "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <h2
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "24px",
                          fontWeight: "700",
                          color: "white",
                          textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        {selectedPlayer.firstName} {selectedPlayer.lastName}
                      </h2>
                      {selectedPlayer.knownName && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            color: "rgba(255, 255, 255, 0.9)",
                            fontStyle: "italic",
                            fontWeight: "400",
                          }}
                        >
                          "{selectedPlayer.knownName}"
                        </p>
                      )}
                    </div>
                  </div>
                  <motion.button
                    style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      color: "white",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      backdropFilter: "blur(10px)",
                    }}
                    onClick={() => setSelectedPlayer(null)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span
                      className="material-icons"
                      style={{ fontSize: "20px" }}
                    >
                      close
                    </span>
                  </motion.button>
                </div>
              </div>

              {/* Content */}
              {isLoadingPlayerDetails && !selectedPlayer ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "48px",
                    background: "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "16px",
                      color: "#6366f1",
                    }}
                  >
                    <motion.span
                      className="material-icons"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      style={{ fontSize: "48px" }}
                    >
                      sync
                    </motion.span>
                    <span style={{ fontSize: "16px", fontWeight: "500" }}>
                      Loading player details...
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    padding: "24px",
                    overflowY: "auto",
                    background: "#f8fafc",
                  }}
                >
                  {/* Show info message if using fallback data */}
                  {selectedPlayer &&
                    !isLoadingPlayerDetails &&
                    !detailedPlayerData && (
                      <div
                        style={{
                          background: "#fef3c7",
                          border: "1px solid #fbbf24",
                          borderRadius: "8px",
                          padding: "12px",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          className="material-icons"
                          style={{ fontSize: "20px", color: "#f59e0b" }}
                        >
                          info
                        </span>
                        <span style={{ fontSize: "14px", color: "#92400e" }}>
                          Showing basic player information. Some detailed
                          statistics may not be available.
                        </span>
                      </div>
                    )}

                  <div style={{ display: "grid", gap: "24px" }}>
                    {/* Personal Information */}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "0 0 16px 0",
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        <span
                          className="material-icons"
                          style={{ fontSize: "22px", color: "#6366f1" }}
                        >
                          person
                        </span>
                        Personal Information
                      </h3>
                      <div style={{ display: "grid", gap: "12px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#6366f1" }}
                            >
                              badge
                            </span>
                            Full Name
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {selectedPlayer.firstName} {selectedPlayer.lastName}
                          </div>
                        </div>
                        {selectedPlayer.knownName && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              background: "#f8fafc",
                              borderRadius: "8px",
                              borderLeft: "4px solid #6366f1",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px", color: "#6366f1" }}
                              >
                                star_border
                              </span>
                              Known As
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {selectedPlayer.knownName}
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#6366f1" }}
                            >
                              sports
                            </span>
                            Position
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {selectedPlayer.position || "-"}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#6366f1" }}
                            >
                              business
                            </span>
                            Club
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {selectedPlayer.clubName || "-"}
                          </div>
                        </div>
                        {selectedPlayer.shirtNumber && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              background: "#f8fafc",
                              borderRadius: "8px",
                              borderLeft: "4px solid #6366f1",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px", color: "#6366f1" }}
                              >
                                tag
                              </span>
                              Shirt Number
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {selectedPlayer.shirtNumber}
                            </div>
                          </div>
                        )}
                        {selectedPlayer.nationality && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              background: "#f8fafc",
                              borderRadius: "8px",
                              borderLeft: "4px solid #6366f1",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px", color: "#6366f1" }}
                              >
                                flag
                              </span>
                              Nationality
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {selectedPlayer.nationality}
                            </div>
                          </div>
                        )}
                        {selectedPlayer.age && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              background: "#f8fafc",
                              borderRadius: "8px",
                              borderLeft: "4px solid #6366f1",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px", color: "#6366f1" }}
                              >
                                cake
                              </span>
                              Age
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {selectedPlayer.age} years
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#6366f1" }}
                            >
                              check_circle
                            </span>
                            Status
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                background:
                                  selectedPlayer.active === "yes"
                                    ? "#dcfce7"
                                    : "#fef2f2",
                                color:
                                  selectedPlayer.active === "yes"
                                    ? "#166534"
                                    : "#dc2626",
                              }}
                            >
                              {selectedPlayer.active === "yes"
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Performance Statistics */}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "0 0 16px 0",
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        <span
                          className="material-icons"
                          style={{ fontSize: "22px", color: "#6366f1" }}
                        >
                          analytics
                        </span>
                        Performance Statistics
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: "16px",
                        }}
                      >
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                            transition: "transform 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              background: "rgba(255, 255, 255, 0.2)",
                              padding: "8px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "20px", color: "white" }}
                            >
                              star
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                lineHeight: 1,
                                marginBottom: "4px",
                              }}
                            >
                              {selectedPlayer.totalPoints || 0}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                opacity: 0.9,
                                fontWeight: "500",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Total Points
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            color: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            boxShadow: "0 4px 12px rgba(240, 147, 251, 0.3)",
                            transition: "transform 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              background: "rgba(255, 255, 255, 0.2)",
                              padding: "8px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "20px", color: "white" }}
                            >
                              today
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                lineHeight: 1,
                                marginBottom: "4px",
                              }}
                            >
                              {selectedPlayer.matchDayPoints || 0}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                opacity: 0.9,
                                fontWeight: "500",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Match Day Points
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                            color: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            boxShadow: "0 4px 12px rgba(79, 172, 254, 0.3)",
                            transition: "transform 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              background: "rgba(255, 255, 255, 0.2)",
                              padding: "8px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "20px", color: "white" }}
                            >
                              sports_soccer
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                lineHeight: 1,
                                marginBottom: "4px",
                              }}
                            >
                              {selectedPlayer.goals || 0}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                opacity: 0.9,
                                fontWeight: "500",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Goals
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                            color: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            boxShadow: "0 4px 12px rgba(67, 233, 123, 0.3)",
                            transition: "transform 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              background: "rgba(255, 255, 255, 0.2)",
                              padding: "8px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "20px", color: "white" }}
                            >
                              assist_walker
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                lineHeight: 1,
                                marginBottom: "4px",
                              }}
                            >
                              {selectedPlayer.assists || 0}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                opacity: 0.9,
                                fontWeight: "500",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Assists
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Match Statistics */}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "0 0 16px 0",
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        <span
                          className="material-icons"
                          style={{ fontSize: "22px", color: "#6366f1" }}
                        >
                          timeline
                        </span>
                        Match Statistics
                      </h3>
                      <div style={{ display: "grid", gap: "12px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#6366f1" }}
                            >
                              event
                            </span>
                            Matches Played
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {selectedPlayer.matchesPlayed || 0}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#f59e0b" }}
                            >
                              warning
                            </span>
                            Yellow Cards
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {selectedPlayer.yellowCards || 0}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            borderLeft: "4px solid #6366f1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontWeight: "600",
                              color: "#475569",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px", color: "#ef4444" }}
                            >
                              error
                            </span>
                            Red Cards
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {selectedPlayer.redCards || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Data */}
                    {(selectedPlayer.id ||
                      selectedPlayer._id ||
                      selectedPlayer.createdAt ||
                      selectedPlayer.updatedAt) && (
                      <div
                        style={{
                          background: "white",
                          borderRadius: "12px",
                          padding: "20px",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <h3
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            margin: "0 0 16px 0",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          <span
                            className="material-icons"
                            style={{ fontSize: "22px", color: "#6366f1" }}
                          >
                            info
                          </span>
                          System Information
                        </h3>
                        <div style={{ display: "grid", gap: "12px" }}>
                          {selectedPlayer.id && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px",
                                background: "#f8fafc",
                                borderRadius: "8px",
                                borderLeft: "4px solid #6366f1",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  fontWeight: "600",
                                  color: "#475569",
                                  fontSize: "14px",
                                }}
                              >
                                <span
                                  className="material-icons"
                                  style={{ fontSize: "18px", color: "#6366f1" }}
                                >
                                  fingerprint
                                </span>
                                Player ID
                              </div>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#1e293b",
                                  fontSize: "14px",
                                }}
                              >
                                {selectedPlayer.id}
                              </div>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              background: "#f8fafc",
                              borderRadius: "8px",
                              borderLeft: "4px solid #6366f1",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px", color: "#6366f1" }}
                              >
                                admin_panel_settings
                              </span>
                              Admin Approved
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  background: selectedPlayer.isAdminApproved
                                    ? "#dcfce7"
                                    : "#fef2f2",
                                  color: selectedPlayer.isAdminApproved
                                    ? "#166534"
                                    : "#dc2626",
                                }}
                              >
                                {selectedPlayer.isAdminApproved
                                  ? "Approved"
                                  : "Not Approved"}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              background: "#f8fafc",
                              borderRadius: "8px",
                              borderLeft: "4px solid #6366f1",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px", color: "#6366f1" }}
                              >
                                schedule
                              </span>
                              First Detected
                            </div>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {formatDateTime(getDetectedAtValue(selectedPlayer))}
                            </div>
                          </div>
                          {selectedPlayer.profileImage && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px",
                                background: "#f8fafc",
                                borderRadius: "8px",
                                borderLeft: "4px solid #6366f1",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  fontWeight: "600",
                                  color: "#475569",
                                  fontSize: "14px",
                                }}
                              >
                                <span
                                  className="material-icons"
                                  style={{ fontSize: "18px", color: "#6366f1" }}
                                >
                                  image
                                </span>
                                Profile Image
                              </div>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#1e293b",
                                  fontSize: "14px",
                                  maxWidth: "300px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {selectedPlayer.profileImage}
                              </div>
                            </div>
                          )}
                          {selectedPlayer.createdAt && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px",
                                background: "#f8fafc",
                                borderRadius: "8px",
                                borderLeft: "4px solid #6366f1",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  fontWeight: "600",
                                  color: "#475569",
                                  fontSize: "14px",
                                }}
                              >
                                <span
                                  className="material-icons"
                                  style={{ fontSize: "18px", color: "#6366f1" }}
                                >
                                  schedule
                                </span>
                                Created At
                              </div>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#1e293b",
                                  fontSize: "14px",
                                }}
                              >
                                {new Date(
                                  selectedPlayer.createdAt
                                ).toLocaleString()}
                              </div>
                            </div>
                          )}
                          {selectedPlayer.updatedAt && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px",
                                background: "#f8fafc",
                                borderRadius: "8px",
                                borderLeft: "4px solid #6366f1",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  fontWeight: "600",
                                  color: "#475569",
                                  fontSize: "14px",
                                }}
                              >
                                <span
                                  className="material-icons"
                                  style={{ fontSize: "18px", color: "#6366f1" }}
                                >
                                  update
                                </span>
                                Last Updated
                              </div>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#1e293b",
                                  fontSize: "14px",
                                }}
                              >
                                {new Date(
                                  selectedPlayer.updatedAt
                                ).toLocaleString()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div
                style={{
                  background: "white",
                  padding: "20px 24px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "center",
                  borderRadius: 0,
                }}
              >
                <motion.button
                  onClick={() => setSelectedPlayer(null)}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="material-icons" style={{ fontSize: "18px" }}>
                    check
                  </span>
                  Close
                </motion.button>
              </div>
            </div>
          </Box>
        </Modal>
      )}

      {/* Edit Player Modal */}
      {editingPlayer && (
        <Modal
          open={!!editingPlayer}
          onClose={() => {
            setEditingPlayer(null);
            setEditFormData({});
            setEditFieldMeta({});
            setManualOverrideState({});
            setFieldEditModeState({});
            setSelectedFile(null);
          }}
          aria-labelledby="edit-player-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "700px" },
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 0,
              maxHeight: "90vh",
              overflow: "auto",
              p: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                maxHeight: "90vh",
                overflow: "hidden",
                background: "#ffffff",
                borderRadius: 0,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #34a853 0%, #4caf50 100%)",
                  color: "white",
                  padding: "20px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: 0,
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    color: "white",
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "24px" }}>
                    edit
                  </span>
                  Edit Player: {editingPlayer.firstName}{" "}
                  {editingPlayer.lastName}
                </h2>
                <motion.button
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => {
                    setEditingPlayer(null);
                    setEditFormData({});
                    setEditFieldMeta({});
                    setManualOverrideState({});
                    setFieldEditModeState({});
                    setSelectedFile(null);
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span className="material-icons" style={{ fontSize: "18px" }}>
                    close
                  </span>
                </motion.button>
              </div>

              {/* Content */}
              <div
                style={{
                  flex: 1,
                  padding: "24px",
                  overflowY: "auto",
                  background: "#f8fafc",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    maxWidth: "100%",
                    margin: "0 auto",
                  }}
                >
                  <div
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "20px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: "3px solid #34a853",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#ffffff",
                          flexShrink: 0,
                        }}
                      >
                        {editFormData.profileImage ? (
                          <img
                            src={getPlayerImageUrl(editFormData.profileImage)}
                            alt="Player"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.querySelector(
                                ".material-icons"
                              ).style.display = "flex";
                            }}
                          />
                        ) : null}
                        <span
                          className="material-icons"
                          style={{
                            fontSize: "40px",
                            color: "#34a853",
                            display: editFormData.profileImage
                              ? "none"
                              : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          person
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: "260px" }}>
                        <h4
                          style={{
                            margin: "0 0 10px 0",
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          Player Photo
                        </h4>
                        <p
                          style={{
                            margin: "0 0 8px",
                            fontSize: "13px",
                            color: "#64748b",
                            lineHeight: 1.5,
                          }}
                        >
                          Upload a high-quality photo of the player. The CDN upload
                          happens immediately, but the new image is published to the
                          app only after you save the player.
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setSelectedFile(file);
                              const maxSize = 5 * 1024 * 1024;
                              if (file.size > maxSize) {
                                alert(
                                  `File size must be less than 5MB. Your file is ${(
                                    file.size /
                                    (1024 * 1024)
                                  ).toFixed(2)}MB`
                                );
                                e.target.value = null;
                                setSelectedFile(null);
                                return;
                              }

                              try {
                                setIsSubmittingEdit(true);
                                const uploadResult = await uploadService.uploadWithStandardName(
                                  file,
                                  "player",
                                  editingPlayer.id,
                                  (progress) => {
                                    console.log(`Upload Progress: ${progress}%`);
                                  }
                                );

                                if (!uploadResult.success) {
                                  throw new Error(uploadResult.error || "Upload failed");
                                }

                                const cdnUrl = uploadResult.url;
                                handleEditFormChange("profileImage", cdnUrl);
                                setManualOverrideState((prev) => ({
                                  ...prev,
                                  profileImage: true,
                                }));
                                alert("Image uploaded. Save changes to publish it.");
                                setSelectedFile(null);
                              } catch (error) {
                                console.error("Error uploading image:", error);
                                if (error.code === "ERR_NETWORK") {
                                  alert(
                                    "Network error occurred. Please check:\n" +
                                    "1. File size is less than 5MB\n" +
                                    "2. You have a stable internet connection\n" +
                                    "3. The server is accessible"
                                  );
                                } else if (error.response?.status === 413) {
                                  alert("File too large. Maximum size is 5MB");
                                } else if (error.response) {
                                  alert(
                                    `Upload failed: ${error.response.data?.error || error.response.data?.message || error.response.statusText}`
                                  );
                                } else {
                                  alert("Failed to upload image: " + error.message);
                                }
                              } finally {
                                setIsSubmittingEdit(false);
                                setSelectedFile(null);
                              }
                            }
                          }}
                          style={{
                            padding: "8px 12px",
                            border: "2px solid #34a853",
                            borderRadius: "6px",
                            fontSize: "14px",
                            background: "white",
                            cursor: "pointer",
                          }}
                        />
                        {selectedFile && (
                          <p
                            style={{
                              margin: "8px 0 0",
                              fontSize: "12px",
                              color: "#64748b",
                            }}
                          >
                            Selected file size:{" "}
                            {(selectedFile.size / (1024 * 1024)).toFixed(2)}MB
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "12px",
                      padding: "16px 18px",
                      color: "#1e3a8a",
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    Text and static fields use direct manual overrides. Feed-driven
                    numeric fields use an admin <strong>+/- adjustment</strong>, so
                    future Opta updates still flow through and your correction stays on
                    top until you click <strong>Use Auto</strong>. If you need to lock
                    one exact number, enable <strong>Hard override this value</strong>
                    on that field.
                  </div>

                  {PLAYER_EDIT_SECTIONS.filter(
                    (section) => !section.goalkeeperOnly || isGoalkeeperEditing
                  ).map((section) => (
                    <div
                      key={section.title}
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        padding: "20px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "0 0 16px 0",
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        <span
                          className="material-icons"
                          style={{ fontSize: "22px", color: "#34a853" }}
                        >
                          {section.icon}
                        </span>
                        {section.title}
                      </h3>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "16px",
                        }}
                      >
                        {section.fields.map((fieldKey) => renderEditField(fieldKey))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  background: "white",
                  padding: "20px 24px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "16px",
                  justifyContent: "flex-end",
                  borderRadius: 0,
                }}
              >
                <motion.button
                  onClick={() => {
                    setEditingPlayer(null);
                    setEditFormData({});
                    setEditFieldMeta({});
                    setManualOverrideState({});
                    setFieldEditModeState({});
                    setSelectedFile(null);
                  }}
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={handleSaveEdit}
                  disabled={isSubmittingEdit}
                  style={{
                    background:
                      "linear-gradient(135deg, #34a853 0%, #4caf50 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: isSubmittingEdit ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(52, 168, 83, 0.3)",
                    opacity: isSubmittingEdit ? 0.7 : 1,
                  }}
                  whileHover={{ scale: isSubmittingEdit ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmittingEdit ? 1 : 0.98 }}
                >
                  {isSubmittingEdit ? (
                    <>
                      <span
                        className="material-icons spin"
                        style={{ fontSize: "18px" }}
                      >
                        sync
                      </span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span
                        className="material-icons"
                        style={{ fontSize: "18px" }}
                      >
                        save
                      </span>
                      Save Changes
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </Box>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deletingPlayer && (
        <Modal
          open={!!deletingPlayer}
          onClose={() => setDeletingPlayer(null)}
          aria-labelledby="delete-player-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "400px" },
              bgcolor: "background.paper",
              height: "auto",
              boxShadow: 24,
              borderRadius: 0,
              p: 0,
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#ffffff",
                borderRadius: 0,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  padding: "20px 24px",
                  borderRadius: 0,
                  flexShrink: 0,
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    color: "white",
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ fontSize: "20px", color: "white" }}
                  >
                    warning
                  </span>
                  Confirm Deletion
                </h2>
              </div>

              {/* Content */}
              <div
                style={{
                  padding: "18px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    color: "#374151",
                    fontWeight: 500,
                  }}
                >
                  Are you sure you want to delete this player?
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    padding: "14px",
                    background: "#f3f4f6",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <strong
                    style={{
                      fontSize: "17px",
                      color: "#1e293b",
                      fontWeight: 600,
                    }}
                  >
                    {deletingPlayer.firstName} {deletingPlayer.lastName}
                  </strong>
                  {deletingPlayer.knownName && (
                    <span
                      style={{
                        fontSize: "15px",
                        color: "#374151",
                        fontWeight: 500,
                        fontStyle: "italic",
                      }}
                    >
                      "{deletingPlayer.knownName}"
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "15px",
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    {deletingPlayer.clubName || "No club"}
                  </span>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    color: "#374151",
                    fontWeight: 500,
                    fontStyle: "italic",
                  }}
                >
                  This action cannot be undone.
                </p>
              </div>

              {/* Footer */}
              <div
                style={{
                  background: "white",
                  padding: "16px 20px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "16px",
                  justifyContent: "flex-end",
                  borderRadius: 0,
                  flexShrink: 0,
                }}
              >
                <motion.button
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    minWidth: "120px",
                    justifyContent: "center",
                  }}
                  onClick={() => setDeletingPlayer(null)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  style={{
                    background: isDeleting
                      ? "#ef4444"
                      : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                    minWidth: "120px",
                    justifyContent: "center",
                    opacity: isDeleting ? 0.7 : 1,
                  }}
                  onClick={confirmDeletePlayer}
                  disabled={isDeleting}
                  whileHover={{ scale: isDeleting ? 1 : 1.02 }}
                  whileTap={{ scale: isDeleting ? 1 : 0.98 }}
                >
                  {isDeleting ? (
                    <>
                      <span
                        className="material-icons spin"
                        style={{ fontSize: "18px" }}
                      >
                        sync
                      </span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <span
                        className="material-icons"
                        style={{ fontSize: "18px" }}
                      >
                        delete
                      </span>
                      Delete Player
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </Box>
        </Modal>
      )}

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <Modal
          open={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          aria-labelledby="mobile-filter-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "background.paper",
              p: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="tournament-player-mgmt-mobile-filter-modal">
              {/* Header */}
              <div className="tournament-player-mgmt-mobile-filter-header">
                <h3>Filter Players</h3>
                <motion.button
                  className="tournament-player-mgmt-mobile-filter-close"
                  onClick={() => setShowMobileFilters(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span className="material-icons">close</span>
                </motion.button>
              </div>

              {/* Content */}
              <div className="tournament-player-mgmt-mobile-filter-content">
                <div className="tournament-player-mgmt-mobile-filter-group">
                  <label>Search Players</label>
                  <div className="tournament-player-mgmt-filter-box">
                    <span className="tournament-player-mgmt-filter-icon">
                      <span className="material-icons">search</span>
                    </span>
                    <input
                      type="text"
                      className="tournament-player-mgmt-filter-input"
                      placeholder="Search by name, team or nationality..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>

                <div className="tournament-player-mgmt-mobile-filter-group">
                  <label>Position</label>
                  <div className="tournament-player-mgmt-filter-box">
                    <span className="tournament-player-mgmt-filter-icon">
                      <span className="material-icons">sports</span>
                    </span>
                    <div className="tournament-player-mgmt-select-wrapper">
                      <select
                        className="tournament-player-mgmt-native-select"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                      >
                        <option value="">All Positions</option>
                        {positions.map((pos) => (
                          <option key={pos} value={pos}>
                            {pos}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="tournament-player-mgmt-mobile-filter-group">
                  <label>Team</label>
                  <div className="tournament-player-mgmt-filter-box">
                    <span className="tournament-player-mgmt-filter-icon">
                      <span className="material-icons">groups</span>
                    </span>
                    <div className="tournament-player-mgmt-select-wrapper">
                      <select
                        className="tournament-player-mgmt-native-select"
                        value={clubName}
                        onChange={(e) => setClubName(e.target.value)}
                      >
                        <option value="">All Teams</option>
                        {teamOptions.map((team) => (
                          <option key={team} value={team}>
                            {team}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="tournament-player-mgmt-mobile-filter-group">
                  <label>Nationality</label>
                  <div className="tournament-player-mgmt-filter-box">
                    <span className="tournament-player-mgmt-filter-icon">
                      <span className="material-icons">flag</span>
                    </span>
                    <div className="tournament-player-mgmt-select-wrapper">
                      <select
                        className="tournament-player-mgmt-native-select"
                        value={nationality}
                        onChange={(e) => setNationality(e.target.value)}
                      >
                        <option value="">All Nationalities</option>
                        {nationalityOptions.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="tournament-player-mgmt-mobile-filter-group">
                  <label>Status</label>
                  <div className="tournament-player-mgmt-filter-box">
                    <span className="tournament-player-mgmt-filter-icon">
                      <span className="material-icons">check_circle</span>
                    </span>
                    <div className="tournament-player-mgmt-select-wrapper">
                      <select
                        className="tournament-player-mgmt-native-select"
                        value={active}
                        onChange={(e) => setActive(e.target.value)}
                      >
                        <option value="">All Status</option>
                        {activeStatuses.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {!isReviewQueueMode && (
                  <div className="tournament-player-mgmt-mobile-filter-group">
                    <label>Admin Approval</label>
                    <div className="tournament-player-mgmt-filter-box">
                      <span className="tournament-player-mgmt-filter-icon">
                        <span className="material-icons">verified_user</span>
                      </span>
                      <div className="tournament-player-mgmt-select-wrapper">
                        <select
                          className="tournament-player-mgmt-native-select"
                          value={adminApproved}
                          onChange={(e) => setAdminApproved(e.target.value)}
                        >
                          <option value="">All Admin Approval</option>
                          {adminApprovedStatuses.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="tournament-player-mgmt-mobile-filter-actions">
                <motion.button
                  className="tournament-player-mgmt-mobile-clear-btn"
                  onClick={clearAllFilters}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="material-icons">clear_all</span>
                  Clear All Filters
                </motion.button>
                <motion.button
                  className="tournament-player-mgmt-mobile-apply-btn"
                  onClick={() => setShowMobileFilters(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="material-icons">check</span>
                  Apply Filters
                </motion.button>
              </div>
            </div>
          </Box>
        </Modal>
      )}
    </motion.div>
  );
}

export default TournamentPlayerManagement;
